/* global TimeoutId */
import { CallStage } from 'app/containers/call/CallStateContext';
import {
  defaultLessonUIState,
  ILessonUIState,
  START_COUNTDOWN_THRESHOLD,
  IMPENDING_END_THRESHOLD,
  IMPENDING_START_THRESHOLD,
  LessonButtonPositionStage,
  LessonButtonStage,
  LessonPhase,
  LessonSoundStage,
  LessonTimeStage,
} from 'app/models/ui/call/lesson/constants';
import LessonBookingRole from 'app/constants/LessonBookingRole';

type LessonPhaseEntries<T> = [
  [LessonPhase.StartCountdown, T],
  [LessonPhase.ImpendingStart, T],
  [LessonPhase.StartElapsed, T],
  [LessonPhase.GracePeriod, T],
  [LessonPhase.ImpendingEnd, T],
  [LessonPhase.EndElapsed, T],
];

/**
 * Creates an iterable of entries that indicate the times that each phase
 * will be elapsed.
 *
 * @param end The scheduled end time, but not necessarily will end.
 * @param lockTime May not be present, but is the canonical end time.
 */
const getLessonPhaseTimes = (start: Date, end: Date, lockTime?: Date): LessonPhaseEntries<Date | null> => {
  return [
    [LessonPhase.StartCountdown, new Date(start.valueOf() - START_COUNTDOWN_THRESHOLD)],
    [LessonPhase.ImpendingStart, new Date(start.valueOf() - IMPENDING_START_THRESHOLD)],
    [LessonPhase.StartElapsed, new Date(start.valueOf())],
    [LessonPhase.GracePeriod, new Date(end.valueOf())],
    [LessonPhase.ImpendingEnd, lockTime ? new Date(lockTime.valueOf() - IMPENDING_END_THRESHOLD) : null],
    [LessonPhase.EndElapsed, lockTime ? new Date(lockTime.valueOf()) : null],
  ];
};

export const getLessonPhase = (now: Date, start: Date, end: Date, lockTime?: Date): LessonPhase => {
  const lessonPhaseTimes = getLessonPhaseTimes(start, end, lockTime);

  // Grab the highest ranked lesson phase whose time is greater than now.
  return lessonPhaseTimes.reduce(
    (prev, [phase, time]) => (time && now >= time ? Math.max(phase, prev) : prev),
    LessonPhase.None,
  );
};

/**
 * Subscribes to notifications when lesson phase changes.
 *
 * @returns An unsubscribe method.
 */
export const notifyLessonPhaseChange = (
  now: Date,
  onChange: (lessonPhase: LessonPhase) => void,
  start: Date,
  end: Date,
  lockTime?: Date,
  dependencies: {
    clearTimeout: (timeoutId: TimeoutId) => void,
    setTimeout: (callback: (() => void), ms: number) => TimeoutId,
  } = {
    // setTimeout/clearTimeout need to be wrapped, because when called as
    // `dependencies.setTimeout(...)` for example, it will have the wrong
    // value for `this` and throw an error.
    clearTimeout: (timeoutId) => clearTimeout(timeoutId),
    setTimeout: (callback, ms) => setTimeout(callback, ms),
  },
) => {
  const lessonPhaseTimes = getLessonPhaseTimes(start, end, lockTime);

  // Get all pending times that haven't elapsed.
  const pending = lessonPhaseTimes.filter(([, time]) => time && time > now);
  const timeoutIds = pending.map(([phase, time]) => {
    if (time && time > now) {
      return dependencies.setTimeout(() => onChange(phase), time.valueOf() - now.valueOf());
    } else {
      return undefined;
    }
  });

  return () => {
    timeoutIds.forEach((timeoutId) => {
      if (timeoutId) {
        dependencies.clearTimeout(timeoutId);
      }
    });
  };
};

/**
 * Handles lesson UI concerns that are not consolidated into a single rendering path of the application.
 * Based on several factors:
 *
 * 1. Time relative to start time, end time and lock time.
 * 2. The state of the call, (locked, unlocked, init).
 * 3. The role of the user.
 */
export const getLessonUIState = (
  phase: LessonPhase,
  callStage: CallStage,
  lessonRole: LessonBookingRole,
): ILessonUIState => {
  if (
    callStage === CallStage.PostLocked ||
    callStage === CallStage.Unknown ||
    lessonRole !== LessonBookingRole.Student
  ) {
    return {
      ...defaultLessonUIState,
      phase,
    };
  }

  switch (callStage) {
    case CallStage.Active: {
      if (phase >= LessonPhase.GracePeriod) {
        return {
          button: LessonButtonStage.ShowEndUrgent,
          buttonPosition: LessonButtonPositionStage.Notification,
          phase,
          sound: LessonSoundStage.None,
          time: LessonTimeStage.Urgent,
        };
      }

      return {
        button: LessonButtonStage.ShowEnd,
        buttonPosition: LessonButtonPositionStage.TimeDetails,
        phase,
        sound: LessonSoundStage.None,
        time: LessonTimeStage.None,
      };
    }

    case CallStage.AwaitingActivation: {
      if (phase >= LessonPhase.StartElapsed) {
        return {
          button: LessonButtonStage.ShowReadyPulsing,
          buttonPosition: LessonButtonPositionStage.TimeDetails,
          phase,
          sound: LessonSoundStage.Start,
          time: LessonTimeStage.None,
        };
      }

      if (phase >= LessonPhase.ImpendingStart) {
        return {
          button: LessonButtonStage.ShowReadyPulsing,
          buttonPosition: LessonButtonPositionStage.TimeDetails,
          phase,
          sound: LessonSoundStage.None,
          time: LessonTimeStage.None,
        };
      }

      return {
        button: LessonButtonStage.None,
        buttonPosition: LessonButtonPositionStage.TimeDetails,
        phase,
        sound: LessonSoundStage.None,
        time: LessonTimeStage.None,
      };
    }
  }
};
